/* eslint-disable react-hooks/exhaustive-deps */
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useRecoilCallback, useRecoilState, useSetRecoilState } from "recoil";
import _ from "underscore";
import { useParams } from "react-router-dom";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import {
    AncillarySubtype,
    getRatePlan,
    getTotalToBePaid,
    IGetBookingByIdWithMetaReturn,
    IGetRatePlanReturn,
    useCouponQueryObserver,
    useProductQuery,
    useRatePlanQueryObserver,
    IGetCompanyReturn,
    getCompanyData,
    getMobilePrefixes,
    getTotalRentalDays,
    RESPONSE_CHECK_CODE,
    getCompany
} from "../../../api/services";
import { CreditType, FirstInvoiceRecipient, InvoiceType, PaymentTiming, ProductCategory, ReactQueryKey, RentalType, RouteParams} from "../../../types";
import {
    LOCALE_DATE_TIME_FORMAT,
    getCarGroupCode,
    setBookingOwnerPropertyCompanyCustomer,
    LOCALE_DATE_FORMAT,
    LOCALE_ISO_DATE_TIME_FORMAT,
    setBookingOwnerPropertyPersonCustomer
} from "../../../utils";
import { AccordionsExpanded, IFormValues } from "../types";
import { editAccordionExpandedState, editCarsAvailabilitiesParamsState, editCustomerBillingDataB2B, editIsPersonBAState, editRatePlanOfferIDState, editTotalRentalDaysState, recalculationTotalToBePaid } from "../recoil";


const StateManager = () => {

    const { t } = useTranslation('booking');
    const {i18n} = useTranslation();
    
    const { globalData, dispatchGlobalData } = useContext(GlobalContext);

    const { id: bookingId } = useParams<RouteParams>();

    const [totalRentalDays, setTotalRentalDays] = useRecoilState(editTotalRentalDaysState);
    const setCarsAvailabilitiesParams = useSetRecoilState(editCarsAvailabilitiesParamsState);
    const setRecalculationTotalToBePaid  = useSetRecoilState(recalculationTotalToBePaid);
    const setIsPersonBA = useSetRecoilState(editIsPersonBAState);
    const setAccordionExpanded = useRecoilCallback(({ set }) => (accordionName: keyof AccordionsExpanded, value: boolean) => {
        set(editAccordionExpandedState(accordionName), value);
    });
    const setCustomerBillingDataB2B = useSetRecoilState(editCustomerBillingDataB2B);
    const [editRatePlanOfferID, setEditRatePlanOfferID] = useRecoilState(editRatePlanOfferIDState);

    const formContext = useFormContext<IFormValues>();

    const formState = useFormState<IFormValues>();

    const basicDetails = useWatch<IFormValues, "basicDetails">({
        name: 'basicDetails'
    });
    const extraServices = useWatch<IFormValues, "extraServices">({
        name: 'extraServices'
    });
    const firstDriver = useWatch<IFormValues, "firstDriver">({
        name: 'firstDriver'
    });
    const additionalDriversAncillary = useWatch<IFormValues>({
        name: `extraServices.ancillary.${AncillarySubtype.AdditionalDrivers}`
    });
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenant" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealer" });

    const [dateModified, setDateModified] = useState<boolean>(false);
    const [mainDetailsModified, setMainDetailsModified] = useState<boolean>(false);

    const queryClient = useQueryClient();

    const productQuery = useProductQuery({
        segment: basicDetails.rentalType,
        businessContinuityInsToolMode: false,
        tenant: tenant as string,
        dealer: dealer as string
    }, {
        refetchOnMount: false
    });

    const couponQuery = useCouponQueryObserver({tenant: basicDetails.tenant as string, dealer: basicDetails.dealer as string});
    const ratePlanQuery = useRatePlanQueryObserver({tenant: basicDetails.tenant as string, dealer: basicDetails.dealer as string});

    useEffect(() => {
        if (
            basicDetails.pickupDate === null ||
            basicDetails.dropoffDate === null ||
            !basicDetails.pickupDate.isValid() ||
            !basicDetails.dropoffDate.isValid() ||
            basicDetails.pickupDate.isAfter(basicDetails.dropoffDate)
        ) {
            setTotalRentalDays(0);
        }
        else {
            calculateTotRentalDays();
        }
    }, [basicDetails.pickupDate, basicDetails.dropoffDate]);

    const calculateTotRentalDays = async () => {
        if(basicDetails.pickupDate === null ||
            basicDetails.dropoffDate === null) {
            return;
        }

        const totalRentalDays = await getTotalRentalDays(
            {pickupDate: basicDetails.pickupDate.format(LOCALE_DATE_TIME_FORMAT),
                dropoffDate: basicDetails.dropoffDate.format(LOCALE_DATE_TIME_FORMAT)
            }, tenant ?? "");
        setTotalRentalDays(totalRentalDays);

        const bookingQueryData = queryClient.getQueryData<IGetBookingByIdWithMetaReturn>([ReactQueryKey.BookingById, bookingId]);
        let pickupDate = dayjs(bookingQueryData?.booking?.pickupDate).format(LOCALE_DATE_FORMAT)
        let dropoffDate = dayjs(bookingQueryData?.booking?.dropoffDate).format(LOCALE_DATE_FORMAT)

        if(!dateModified && !!pickupDate && !! dropoffDate && !!totalRentalDays) {
            if(!basicDetails.pickupDate?.isSame(pickupDate, 'day') ||
                !basicDetails.dropoffDate?.isSame(dropoffDate, 'day') ||
                totalRentalDays !== bookingQueryData?.booking?.totalRentalDays){
                setDateModified(true);
            }
        }
    }

    useEffect(() => {
        const {
            pickupLocation,
            dropoffLocation,
            carGroup,
        } = basicDetails;
        const bookingQueryData = queryClient.getQueryData<IGetBookingByIdWithMetaReturn>([ReactQueryKey.BookingById, bookingId]);

        if(!mainDetailsModified &&
            bookingQueryData &&
            pickupLocation &&
            dropoffLocation &&
            carGroup &&
            (
                pickupLocation.id !== bookingQueryData.booking.pickupLocation.id ||
                dropoffLocation.id !== bookingQueryData.booking.dropoffLocation.id ||
                carGroup.acrissCode !== bookingQueryData.booking.carGroup.acrissCode ||
                carGroup.sippCode !== bookingQueryData.booking.carGroup.sippCode
            )
        ) {
            setMainDetailsModified(true);
        }
    }, [
        basicDetails.pickupLocation,
        basicDetails.dropoffLocation,
        basicDetails.carGroup
    ]);

    useEffect(() => {
        const {
            pickupLocation,
            dropoffLocation,
            pickupDate,
            dropoffDate,
            carGroup,
            lockGroup,
            customerCode,
            rentalType,
            mileageIncluded
        } = basicDetails;
        if (
            pickupLocation &&
            dropoffLocation &&
            pickupDate &&
            dropoffDate &&
            carGroup &&
            mileageIncluded &&
            ratePlanQuery.data
        ) {
            const ratesId: string[] = []
            ratePlanQuery.data
            .mileageList.filter(ml => ml.value == mileageIncluded)
            .filter(ml => ml.products?.filter(p => p.product?.category?.code == ProductCategory.POA && p.product?.paymentTiming == PaymentTiming.PAY_ON_ARRIVAL)
            .forEach(p => ratesId.push(p.rateId?.toString())));

            formContext.trigger(['basicDetails.pickupDate', 'basicDetails.dropoffDate'])
                .then(valid => {
                    if (valid) {
                        const productQuery = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: rentalType, tenant: tenant as string, dealer: dealer as string}]);
                        setCarsAvailabilitiesParams({
                            pickupDate: pickupDate.toISOString(),
                            dropoffDate: dropoffDate.toISOString(),
                            pickupLocation: pickupLocation,
                            dropoffLocation: dropoffLocation,
                            carGroupCode: getCarGroupCode(carGroup),
                            lockGroup: lockGroup,
                            customerCode: customerCode || null,
                            segment: productQuery?.segmentCode || '',
                            products: productQuery?.products || [],
                            rates: ratesId || [],
                            tenant: tenant as string,
                            dealer: dealer as string
                        });
                    }
                })
            return () => {
                setCarsAvailabilitiesParams(undefined);
            }
        }
    }, [
        basicDetails.pickupLocation,
        basicDetails.pickupDate,
        basicDetails.dropoffLocation,
        basicDetails.dropoffDate,
        basicDetails.carGroup,
        basicDetails.lockGroup,
        basicDetails.mileageIncluded,
        ratePlanQuery.data
    ]);

    useEffect(() => {
        const {
            pickupLocation,
            dropoffLocation,
            pickupDate,
            dropoffDate,
            carGroup
        } = basicDetails;

        const bookingQueryData = queryClient.getQueryData<IGetBookingByIdWithMetaReturn>([ReactQueryKey.BookingById, bookingId]);

        const productsQuery = basicDetails.rentalType === RentalType.RETAIL ?
            productQuery.data :
            queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: basicDetails.rentalType, tenant: tenant as string, dealer: dealer as string}]);
        if (
            pickupLocation &&
            dropoffLocation &&
            pickupDate &&
            dropoffDate &&
            carGroup &&
            productsQuery &&
            bookingQueryData &&
            totalRentalDays &&
            (
                dateModified ||
                mainDetailsModified ||
                !pickupDate.isSame(dayjs(bookingQueryData.booking.pickupDate)) ||
                !ratePlanQuery.data ||
                !dropoffDate.isSame(dayjs(bookingQueryData.booking.dropoffDate))
            )
        ) {
            formContext.trigger(['basicDetails.pickupDate', 'basicDetails.dropoffDate'])
                .then( async (valid) => {
                    if (pickupDate.isValid() && dropoffDate.isValid() && pickupDate.isBefore(dropoffDate)) {
                        let firstEdit = false;

                        if( !mainDetailsModified &&
                            totalRentalDays == bookingQueryData.booking.totalRentalDays &&
                            pickupDate.daysInMonth() == dayjs(bookingQueryData.booking.pickupDate).daysInMonth() &&
                            pickupDate.month() == dayjs(bookingQueryData.booking.pickupDate).month() &&
                            pickupDate.year() == dayjs(bookingQueryData.booking.pickupDate).year() &&
                            (
                                pickupDate.hour() !== dayjs(bookingQueryData.booking.pickupDate).hour() ||
                                pickupDate.minute() !== dayjs(bookingQueryData.booking.pickupDate).minute()
                            )
                        )
                        { 
                            firstEdit = true;
                        }

                        const ratePlan = await queryClient.fetchQuery({
                            queryKey: [ReactQueryKey.RatePlan, {tenant: tenant as string, dealer: dealer as string}],
                            queryFn: () => getRatePlan({
                                pickupDate: pickupDate.format(LOCALE_DATE_TIME_FORMAT),
                                dropoffDate: dropoffDate.format(LOCALE_DATE_TIME_FORMAT),
                                pickupLocation: pickupLocation.id,
                                dropoffLocation: dropoffLocation.id,
                                carGroupCode: getCarGroupCode(carGroup),
                                products: productsQuery?.products,
                                businessAgreement: bookingQueryData.booking.customerCode ?? null,
                                offerId: editRatePlanOfferID,
                                firstEdit: firstEdit,
                                profileIdList: globalData?.user?.profiles
                                    .map((p: any) => (
                                        p.id
                                    )),
                                requestDate: firstEdit ? dayjs(bookingQueryData.booking.bookingDatetime).toISOString() : undefined,
                                tenant: tenant as string,
                                dealer: dealer as string
                            })
                        });

                        const ancillary = formContext.getValues('extraServices.ancillary');

                        let newAncillaryObject: Record<string, boolean | number> = {} ;

                        ratePlan.ancillaries.filter(a => ['A', 'Z'].includes(a.ancillaryDetails[0].sellType)).map( a => {
                            const code = a.subtype === AncillarySubtype.AdditionalDrivers ? a.subtype : a.ancillaryCode;
                            newAncillaryObject[code] = (a.maxUnits > 1 || a.mileageNeeded) ? 1 : true;
                        })

                        Object.keys(ancillary).map( elem => {
                            if(ratePlan.ancillaries
                                .some( a => !['A', 'Z'].includes(a.ancillaryDetails[0].sellType) && (a.ancillaryCode === elem ||
                                        (elem === AncillarySubtype.AdditionalDrivers && a.subtype === AncillarySubtype.AdditionalDrivers)))) {
                                newAncillaryObject[elem] = ancillary[elem];
                            }
                        })
                        formContext.setValue('extraServices.ancillary', newAncillaryObject);

                        if(!!ratePlan.offerId && editRatePlanOfferID != ratePlan.offerId)
                            setEditRatePlanOfferID(ratePlan.offerId);
                    }
                }).catch((error: any) => {
                    // @tanstack/react-query CancelledError, caused by resetQueries
                    if (error?.constructor?.name !== 'CancelledError') {
                        throw error
                    }
                })
            return () => {
                queryClient.resetQueries([ReactQueryKey.RatePlan, {tenant: tenant as string, dealer: dealer as string}]);
               // formContext.setValue('extraServices.ancillary', {})
            }
        }
    }, [
        basicDetails.pickupLocation,
        basicDetails.dropoffLocation,
        totalRentalDays,
        basicDetails.carGroup,
        mainDetailsModified,
        basicDetails.pickupDate,
        tenant, dealer,
        basicDetails.dropoffDate,
    ]);
    useEffect(() => {
        const {
            pickupDate,
            dropoffDate,
            mileageIncluded
        } = basicDetails;
        if (
            pickupDate &&
            dropoffDate &&
            mileageIncluded &&
            ratePlanQuery.data &&
            (!couponQuery?.data || couponQuery?.data?.code === RESPONSE_CHECK_CODE.SUCCESS)
        ) {
            formContext.trigger(['basicDetails.pickupDate', 'basicDetails.dropoffDate'])
                .then((valid) => {
                    if (pickupDate.isValid() || dropoffDate.isValid() ||
                                     pickupDate.isBefore(dropoffDate)) {
                        const discountType = formContext.getValues('extraServices.discountType');
                        const promoCode = discountType === 'promo' ?
                            formContext.getValues('extraServices.discountCode') :
                            formContext.getValues('extraServices.fcaBankCardNumber');
                        queryClient.fetchQuery({
                            queryKey: [ReactQueryKey.TotalToBePaid, {tenant: tenant as string, dealer: dealer as string}],
                            queryFn: () => getTotalToBePaid({
                                pickupDate: pickupDate.toISOString(),
                                dropOffDate: dropoffDate.toISOString(),
                                rateDetailIds: ratePlanQuery.data.mileageList.find(m => m.value === mileageIncluded)?.products.map(p => p.rateId) ?? [],
                                ancillaryDetailIds: Object
                                    .entries(formContext.getValues('extraServices.ancillary'))
                                    .reduce((arr: any[], [key, value]: [string, number | boolean]) => {
                                        switch (typeof value) {
                                            case 'number':
                                                const mileageNeeded: boolean = ratePlanQuery.data?.ancillaries.find(a => a.ancillaryCode === key)?.mileageNeeded ?? false;
                                                const ancillaryCode = key === AncillarySubtype.AdditionalDrivers ?
                                                    (ratePlanQuery.data?.ancillaries.find(a => a.subtype === key)?.ancillaryCode ?? '') :
                                                    key;
                                                value !== 0 && arr.push({
                                                    ancillaryCode: ancillaryCode,
                                                    units: mileageNeeded ? null : value,
                                                    mileage: mileageNeeded ? value : null
                                                });
                                                break;
                                            case 'boolean':
                                                value && arr.push({
                                                    ancillaryCode: key,
                                                    units: 1,
                                                    mileage: null
                                                });
                                                break;
                                        }
                                        return arr;
                                    }, []),
                                promo: promoCode !== undefined && couponQuery?.data?.codeServiceType ? [{
                                    code: promoCode,
                                    type: couponQuery.data.codeServiceType
                                }] : [],
                                // come concordato non viene effettuato il check affilietedCode nella fase di edit
                                affiliationCode: formContext.getValues('extraServices.affiliationCode') ?? null,
                                offerId: ratePlanQuery.data.offerId,
                                discountOverpriceSelected: extraServices.discountOverprice || undefined,
                                tenant: tenant as string,
                                dealer: dealer as string,
                                language: i18n?.language ? i18n?.language?.toLowerCase() : 'it'
                            })
                        });
                        // set recoil value for run check stop overbooking
                        const bookingQueryData = queryClient.getQueryData<IGetBookingByIdWithMetaReturn>([ReactQueryKey.BookingById, bookingId]);

                        const productsQuery = basicDetails.rentalType === RentalType.RETAIL ?
                            productQuery.data :
                            queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: basicDetails.rentalType, tenant: tenant as string, dealer: dealer as string}]);
                        if (
                            basicDetails.pickupLocation &&
                            basicDetails.dropoffLocation &&
                            basicDetails.pickupDate &&
                            basicDetails.dropoffDate &&
                            basicDetails.carGroup &&
                            productsQuery &&
                            bookingQueryData &&
                            totalRentalDays &&
                            (
                                basicDetails.pickupLocation.id !== bookingQueryData.booking.pickupLocation.id ||
                                basicDetails.dropoffLocation.id !== bookingQueryData.booking.dropoffLocation.id ||
                                basicDetails.carGroup.acrissCode !== bookingQueryData.booking.carGroup.acrissCode ||
                                basicDetails.carGroup.sippCode !== bookingQueryData.booking.carGroup.sippCode ||
                                totalRentalDays !== bookingQueryData.booking.totalRentalDays ||
                                dateModified
                            )
                        ) {
                            setRecalculationTotalToBePaid(true);
                        }
                    }
                });
            return () => {
                queryClient.resetQueries([ReactQueryKey.TotalToBePaid, {tenant: tenant as string, dealer: dealer as string}])
            };
        }
    }, [
        basicDetails.mileageIncluded,
        JSON.stringify(extraServices.ancillary),
        couponQuery.data,
        extraServices.discountOverprice,
        ratePlanQuery.data?.offerId,
        tenant, dealer
    ]);

    /**
     * AUTOMATIC FETCH OF VERIFY BA
     */
    useEffect(() => {
        const {
            customerCode,
            pickupDate, 
            rentalType,
            pickupLocation,
            dropoffLocation
        } = basicDetails;

        if(
            customerCode &&
            pickupDate &&
            rentalType == RentalType.CORPORATE
        )
        {
            formContext.trigger('basicDetails.pickupDate')
                .then(async valid => {

                    if(valid) {
                        const company = await queryClient.fetchQuery({
                            queryKey: [ReactQueryKey.Product, {segment: rentalType, tenant: tenant as string, dealer: dealer as string}],
                            //queryFn: () => getCompany(customerCode, pickupDate.toISOString(), tenant as string, dealer as string)
                            queryFn: () => {
                                const response = getCompany(customerCode, pickupDate.toISOString(), tenant as string, dealer as string);
                                return response;
                            }
                        });

                        if(company && company.locations && !!pickupLocation && !company.locations.find(location => location.id == pickupLocation.id)) {
                            formContext.setValue('basicDetails.pickupLocation', null);
                        }

                        if(company && company.locations && !!dropoffLocation && !company.locations.find(location => location.id == dropoffLocation.id)) {
                            formContext.setValue('basicDetails.dropoffLocation', null);
                        }
                        
                    }  
                }
            );
        }

    }, [
        basicDetails.pickupDate,
        basicDetails.rentalType,
    ]);

    useEffect(() => {
        if (basicDetails.rentalType === RentalType.RETAIL) {
            // set booking owner from first driver
            for (const [key, value] of Object.entries(firstDriver)) {
                // @ts-ignore
                formContext.setValue((`bookingOwner.${key}`), value);
            }
        }
        else {
            const productAffiliatedQuery = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: basicDetails.rentalType, tenant: tenant as string, dealer: dealer as string}]);

            if (basicDetails.rentalType === RentalType.CORPORATE &&
                productAffiliatedQuery?.firstInvoiceRecipient !== FirstInvoiceRecipient.COMPANY &&
                productAffiliatedQuery?.creditType === CreditType.NO_CREDIT &&
                formContext.getValues('invoice.type') === InvoiceType.PERSON) {
                // set invoice person from first driver
                for (const [key, value] of Object.entries(firstDriver)) {
                    // @ts-ignore
                    formContext.setValue((`invoice.${key}`), value);
                }
            }
        }
    }, [firstDriver]);

    useEffect(() => {
        if (!formState.isSubmitting) {
            if (!_.isEmpty(formState.errors)) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.form'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }

            console.log('errors', formState.errors);
            if (formState.errors.extraServices) {
                setAccordionExpanded('extraServices', true);
            }
            if (formState.errors.firstDriver) {
                setAccordionExpanded('firstDriver', true);
            }
            if (formState.errors.invoice) {
                setAccordionExpanded('invoice', true);
            }
            if (formState.errors.bookingOwner) {
                setAccordionExpanded('bookingOwner', true);
            }
        }
    }, [formState.isSubmitting]);

    /**
     * Change quantity of additional driver
     */
    useEffect(() => {
        let additionalDrivers = formContext.getValues('additionalDrivers');

        if(!additionalDriversAncillary) {
            additionalDrivers = [];

        } else if (additionalDriversAncillary > additionalDrivers?.length) {
            for (let i = additionalDrivers?.length; i < additionalDriversAncillary; i++) {
                additionalDrivers.push({
                    id: '',
                    name: '',
                    surname: '',
                    email: ''
                })
            }
        } else if(additionalDriversAncillary < additionalDrivers?.length) {
            additionalDrivers = [];
            for (let i = 0; i < additionalDriversAncillary; i++) {
                additionalDrivers.push({
                    id: '',
                    name: '',
                    surname: '',
                    email: ''
                })
            }
        }
        formContext.setValue('additionalDrivers', additionalDrivers);

    }, [additionalDriversAncillary]);

    /**  */
    useEffect(() => {
        // Navigate to step CUSTOMER_DETAILS
        if(basicDetails.rentalType === RentalType.CORPORATE) {
            companyData();
        } else {
            setIsPersonBA(false);
            // set booking owner from first driver
            for (const [key, value] of Object.entries(formContext.getValues('firstDriver'))) {
                // @ts-ignore
                formContext.setValue((`bookingOwner.${key}`), value);
            }
        }
    }, [basicDetails.rentalType]);

    const companyData = async () => {
        const productAffiliated = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: basicDetails.rentalType, tenant: tenant as string, dealer: dealer as string}]);

        if(!!productAffiliated) {
            const firstInvoiceRecipient = productAffiliated.firstInvoiceRecipient;
            const params = {
                companyId: productAffiliated.companyId,
                firstInvoiceRecipient: firstInvoiceRecipient,
                creditType: productAffiliated.creditType
            }
            const ownerCompanyResponse = await getCompanyData(params);

            setIsPersonBA(((productAffiliated.firstInvoiceRecipient == FirstInvoiceRecipient.PERSON) || 
            (productAffiliated.firstInvoiceRecipient == FirstInvoiceRecipient.CUSTOMER && 
            productAffiliated.creditType == CreditType.NO_CREDIT && 
            ownerCompanyResponse.bookingOwnerPerson !== null)));

            if(firstInvoiceRecipient == FirstInvoiceRecipient.PERSON) {

                // set booking owner person
                const bookingOwner = setBookingOwnerPropertyPersonCustomer(ownerCompanyResponse.bookingOwnerPerson);
                for (const [key, value] of Object.entries(bookingOwner)) {
                    // @ts-ignore
                    formContext.setValue((`${key}`), value);
                }

                if(ownerCompanyResponse.bookingOwnerPerson.mobilePrefix) {
                    const mobilePrefixes = await queryClient.fetchQuery({
                        queryKey: [ReactQueryKey.MobilePrefixes],
                        queryFn: () => getMobilePrefixes()
                    });

                    const foundMobilePrefix = mobilePrefixes?.find(mp => mp.prefix === ownerCompanyResponse.bookingOwnerPerson.mobilePrefix);
                    formContext.setValue('bookingOwner.mobilePrefix', foundMobilePrefix ?? null);
                }

                // set billing form
                setCustomerBillingDataB2B({
                    customerId: '',
                    sapId: '',
                    companyName: '',
                    vatNumber: '',
                    vatNumberCountry: '',
                    fiscalCode: '',
                    siren: '',
                    siret: '',
                    country: '',
                    city: '',
                    streetName: '',
                    streetNumber: '',
                    postalCode: '',
                    certifiedEmail: '',
                    email: '',
                    invoiceCode: '',
                    uuid: '',
                    telephone: '',
                    mobile: '',
                    mobilePrefix: '',
                    province: '',
                    linkedToBusinessAgreement: false,
                    legalEntity: null,
                    publicAuthority: '',
                    paIdNumber: ''

                });

            }
            else {

                const isPersonBaNoCredit = productAffiliated?.creditType === CreditType.NO_CREDIT && productAffiliated?.firstInvoiceRecipient === FirstInvoiceRecipient.CUSTOMER && ownerCompanyResponse.bookingOwnerPerson !== null;
                const bookingOwner = isPersonBaNoCredit ? setBookingOwnerPropertyPersonCustomer(ownerCompanyResponse?.bookingOwnerPerson) : setBookingOwnerPropertyCompanyCustomer(ownerCompanyResponse?.bookingOwner);
                let bookingOwnerMobilePrefix = isPersonBaNoCredit ? ownerCompanyResponse?.bookingOwnerPerson?.mobilePrefix : ownerCompanyResponse?.bookingOwner?.mobilePrefix
                
                // set booking owner
                for (const [key, value] of Object.entries(bookingOwner)) {
                    // @ts-ignore
                    formContext.setValue((`${key}`), value);
                }

                if(bookingOwnerMobilePrefix) {
                    const mobilePrefixes = await queryClient.fetchQuery({
                        queryKey: [ReactQueryKey.MobilePrefixes],
                        queryFn: () => getMobilePrefixes()
                    });

                    const foundMobilePrefix = mobilePrefixes?.find(mp => mp.prefix === bookingOwnerMobilePrefix);
                    formContext.setValue('bookingOwner.mobilePrefix', foundMobilePrefix ?? null);
                }

                // set billing form
                setCustomerBillingDataB2B(ownerCompanyResponse.billingOwner);

            }
        }
    }

    return <></>;
};

export default StateManager;
